import { resolveAccessToken } from "@/lib/auth";
import { refreshAppTokens } from "@/lib/auth/token-session";
import { getApiConfig } from "@/lib/api/config";
import {
  ApiError,
  extractEnvelopeMessage,
  formatUserErrorMessage,
} from "@/lib/api/errors";

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  params?: Record<string, string | number | undefined>;
  _retry?: boolean;
};

function buildUrl(path: string, params?: RequestOptions["params"]): string {
  const { baseUrl } = getApiConfig();
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const url = new URL(`${baseUrl}${normalizedPath}`);

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== "") {
        url.searchParams.set(key, String(value));
      }
    }
  }

  return url.toString();
}

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { body, params, headers, _retry, ...init } = options;
  const token = await resolveAccessToken();

  const response = await fetch(buildUrl(path, params), {
    ...init,
    headers: {
      ...(body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    body:
      body === undefined
        ? undefined
        : body instanceof FormData
          ? body
          : JSON.stringify(body),
  });

  if (
    !response.ok &&
    response.status === 401 &&
    !_retry &&
    !/\/token\/refresh(?:\?|$|\/)/i.test(path)
  ) {
    const refreshed = await refreshAppTokens();
    if (refreshed?.accessToken) {
      return apiRequest<T>(path, { ...options, _retry: true });
    }
  }

  if (!response.ok) {
    let raw: string | undefined;
    try {
      const payload = await response.json();
      raw = extractEnvelopeMessage(payload);
    } catch {
      // ignore parse errors
    }
    throw new ApiError(
      formatUserErrorMessage(raw, response.status, "generic"),
      response.status,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}
